/**
 * Settings → Sources item column (web.md §4 "Sources list"): the prototype's `settingsList()` rows with
 * each source's numbers — delivered, and how many of them are in each folder now, as counts and as a
 * share of delivered — a sort bar (name, delivered, count or share in a folder; ascending / descending),
 * the kind and status filters (the Filter dialog), pause / resume per row and for a selection.
 * Filter and sort survive leaving the screen and a reload (list-model.ts).
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Folder, SourceId } from '@digester/core';
import { openDialog } from '../../app/dialogs.js';
import { reportError } from '../../app/errors.js';
import { useFolders } from '../../app/hooks.js';
import { showToast } from '../../app/Toast.js';
import { ui, useUi } from '../../app/ui-store.js';
import { useEngine, useEngineQuery } from '../../engine-client/EngineProvider.js';
import { qk } from '../../engine-client/keys.js';
import { Icon } from '../../icons/Icon.js';
import { Count } from '../../ui/Count.js';
import { FolderIcon } from '../../ui/FolderIcon.js';
import { formatCount, formatExact } from '../../ui/format.js';
import { SOURCE_KIND_LABELS, sourceKindIcon } from '../../ui/source-icons.js';
import { Tool } from '../../ui/Tool.js';
import type { ItemListProps } from '../shell/ItemList.js';
import {
  arrangeRows, buildRows, countOf, DELETED, isFiltered, numberFolders, percentText, sortLabel, sortOptions, sourceList, useSourceList,
  type SourceListRow, type SourceSortKey,
} from './list-model.js';

const STATUS_TEXT: Readonly<Record<SourceListRow['health'], string | null>> = {
  ok: null,
  error: 'Last pull failed',
  secret: 'Needs a password or token on this device',
  paused: 'Paused',
};

/** "In Dismissed" → "in Dismissed" (folder names keep their capital). */
const lowerFirst = (text: string): string => text.charAt(0).toLowerCase() + text.slice(1);

const STATUS_FILTER_TEXT = { enabled: 'Enabled', paused: 'Paused', failing: 'Failing' } as const;

interface Cell {
  key: string;
  name: string;
  folder: Folder | null;
}

/** The number cells of a row: every folder in sidebar order, then Deleted; custom folders only when non-zero. */
function cellsOf(row: SourceListRow, cells: readonly Cell[], sortedKey: string | null): Cell[] {
  return cells.filter((c) => c.folder?.builtin === true || countOf(row, c.key) > 0 || c.key === sortedKey);
}

function NumberCell({ cell, row, sorted }: { cell: Cell; row: SourceListRow; sorted: boolean }) {
  const n = countOf(row, cell.key);
  const pct = percentText(n, row.received);
  return (
    <span
      className={sorted ? 'source-number sorted' : 'source-number'}
      title={`${cell.name}: ${formatExact(n)} of ${formatExact(row.received)} delivered (${pct})`}
      data-cell={cell.key}
      data-count={n}
    >
      {cell.folder ? <FolderIcon folder={cell.folder} /> : <Icon name="trash" title="Deleted" />}
      <b>{formatCount(n)}</b>
      <i>{pct}</i>
    </span>
  );
}

export function SourceList({ category, selectedId }: ItemListProps) {
  const navigate = useNavigate();
  const engine = useEngine();
  const search = useUi((s) => s.settingsSearch);
  const state = useSourceList();
  const folders = useFolders();
  const sources = useEngineQuery(qk.sources(), (e) => e.sources.list());
  const overview = useEngineQuery(qk.sourceOverview(), (e) => e.sources.overview()).data ?? [];
  const statuses = useEngineQuery(qk.sourceStatus(), (e) => e.sources.status()).data ?? [];
  const [selected, setSelected] = useState<ReadonlySet<string>>(new Set());
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => buildRows(sources.data ?? [], overview, statuses), [sources.data, overview, statuses]);
  const shown = useMemo(() => arrangeRows(rows, state, search), [rows, state, search]);
  const cells = useMemo<Cell[]>(
    () => [...numberFolders(folders).map((f) => ({ key: f.key, name: f.name, folder: folders.find((x) => x.id === f.key) ?? null })), { key: DELETED, name: 'Deleted', folder: null }],
    [folders],
  );
  const sortedKey = state.sort.startsWith('count:') || state.sort.startsWith('share:') ? state.sort.slice(6) : null;
  const liveSelected = shown.filter((r) => selected.has(r.source.id));
  const allShownSelected = shown.length > 0 && liveSelected.length === shown.length;

  const toggle = (id: string): void =>
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const setEnabled = async (ids: string[], enabled: boolean): Promise<void> => {
    if (ids.length === 0 || busy) return;
    setBusy(true);
    try {
      const result = await engine.sources.setEnabled(ids as SourceId[], enabled);
      const n = result.changed;
      showToast(n === 0 ? (enabled ? 'Already running' : 'Already paused') : `${enabled ? 'Resumed' : 'Paused'} ${n} source${n === 1 ? '' : 's'}`, result.undoId);
    } catch (error) {
      reportError(error);
    } finally {
      setBusy(false);
    }
  };

  const filtered = isFiltered(state);
  return (
    <section className="settings-list source-list">
      <div className="pane-heading">
        <span>
          <Tool icon="menu" label="Settings categories" onClick={() => ui.set({ mobileOverlay: 'navigation' })} /> {category.name} <Count value={shown.length} />
        </span>
        {category.newLabel ? <Tool icon="plus" label="Add item" onClick={() => navigate(`/settings/${category.id}/new`)} /> : null}
      </div>
      <div className="source-list-bar">
        <input
          type="checkbox"
          className="source-check"
          aria-label="Select all shown"
          checked={allShownSelected}
          ref={(el) => {
            if (el) el.indeterminate = liveSelected.length > 0 && !allShownSelected;
          }}
          onChange={() => setSelected(allShownSelected ? new Set() : new Set(shown.map((r) => r.source.id)))}
        />
        <label className="source-sort">
          <span>Sort</span>
          <select aria-label="Sort sources by" value={state.sort} onChange={(e) => sourceList.set({ sort: e.target.value as SourceSortKey, dir: e.target.value === 'name' ? 'asc' : 'desc' })}>
            {sortOptions(folders).map(([k, label]) => (
              <option key={k} value={k}>
                {label}
              </option>
            ))}
          </select>
        </label>
        <Tool
          icon={state.dir === 'asc' ? 'arrow-up' : 'arrow-down'}
          label={state.dir === 'asc' ? 'Ascending — reverse' : 'Descending — reverse'}
          data-sort-dir={state.dir}
          onClick={() => sourceList.set({ dir: state.dir === 'asc' ? 'desc' : 'asc' })}
        />
        <Tool icon="list-filter" label="Filter sources" pressed={filtered} onClick={() => openDialog('sources-filter', {})} />
      </div>
      {filtered ? (
        <div className="source-list-filters" data-source-filters>
          <span>
            {[...state.kinds.map((k) => SOURCE_KIND_LABELS[k]), ...state.statuses.map((s) => STATUS_FILTER_TEXT[s])].join(' · ')}
          </span>
          <button type="button" className="text-link" onClick={() => sourceList.set({ kinds: [], statuses: [] })}>
            Clear
          </button>
        </div>
      ) : null}
      {liveSelected.length > 0 ? (
        <div className="source-bulk-bar" role="toolbar" aria-label="Selected sources">
          <span>{liveSelected.length} selected</span>
          <button type="button" className="secondary-button" disabled={busy} onClick={() => void setEnabled(liveSelected.map((r) => r.source.id), false)}>
            <Icon name="pause" /> Pause
          </button>
          <button type="button" className="secondary-button" disabled={busy} onClick={() => void setEnabled(liveSelected.map((r) => r.source.id), true)}>
            <Icon name="play" /> Resume
          </button>
          <Tool icon="x" label="Clear selection" onClick={() => setSelected(new Set())} />
        </div>
      ) : null}
      <div className="setting-items">
        {shown.length ? (
          shown.map((r) => {
            const s = r.source;
            const status = s.enabled ? STATUS_TEXT[r.health] : s.disabledReason === 'plan' ? 'Paused: over the plan’s source limit' : STATUS_TEXT.paused;
            const tone = r.health === 'error' ? 'error' : r.health === 'secret' ? 'warn' : null;
            const isSelected = selectedId === s.id;
            return (
              <div
                key={s.id}
                className={['setting-item', 'source-row', isSelected ? 'selected' : '', selected.has(s.id) ? 'checked' : ''].filter(Boolean).join(' ')}
                data-item-id={s.id}
                aria-current={isSelected ? 'true' : undefined}
                data-source-kind={s.kind}
                data-received={r.received}
              >
                <input type="checkbox" className="source-check" aria-label={`Select ${s.name}`} checked={selected.has(s.id)} onChange={() => toggle(s.id)} />
                <button type="button" className="source-row-main" onClick={() => navigate(`/settings/${category.id}/${encodeURIComponent(s.id)}`)}>
                  <span className="setting-item-icon">
                    <Icon name={sourceKindIcon(s.kind)} />
                  </span>
                  <span className="source-row-text">
                    <strong>{s.name}</strong>
                    <small>
                      {SOURCE_KIND_LABELS[s.kind]} · <span data-delivered title={`${formatExact(r.received)} delivered`}>{formatCount(r.received)} delivered</span>
                      {status ? (
                        <>
                          {' · '}
                          <span data-source-status={r.health}>{status}</span>
                        </>
                      ) : null}
                    </small>
                  </span>
                </button>
                {s.archivedAt === null ? (
                  <Tool
                    icon={s.enabled ? 'pause' : 'play'}
                    label={s.enabled ? `Pause ${s.name}` : `Resume ${s.name}`}
                    className="source-pause"
                    disabled={busy}
                    data-action={s.enabled ? 'pause' : 'resume'}
                    onClick={() => void setEnabled([s.id], !s.enabled)}
                  />
                ) : null}
                <i
                  className={['item-status', s.enabled ? 'on' : '', tone ? `tone-${tone}` : ''].filter(Boolean).join(' ')}
                  title={tone === 'error' ? 'Failing' : tone === 'warn' ? 'Needs attention' : s.enabled ? 'Enabled' : 'Paused'}
                />
                <span className="source-numbers" aria-label={`Where ${s.name}'s messages are`}>
                  {cellsOf(r, cells, sortedKey).map((c) => (
                    <NumberCell key={c.key} cell={c} row={r} sorted={c.key === sortedKey} />
                  ))}
                </span>
              </div>
            );
          })
        ) : (
          <div className="empty">
            <h2>{sources.isLoading ? 'Loading…' : rows.length > 0 ? 'No sources match' : 'No items'}</h2>
            {rows.length > 0 && (filtered || search.trim() !== '') ? (
              <button type="button" className="text-link" onClick={() => sourceList.set({ kinds: [], statuses: [] })}>
                Clear filters
              </button>
            ) : null}
          </div>
        )}
      </div>
      <div className="pane-footer source-list-foot" aria-live="polite">
        Sorted by {lowerFirst(sortLabel(state.sort, folders))}, {state.dir === 'asc' ? 'ascending' : 'descending'}. Numbers count every message a source delivered, deleted ones included.
      </div>
    </section>
  );
}
